import { PropsWithChildren, useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore, useAuth, useRouteAccessContext } from '@sib/auth';

export interface ProtectedRouteProps {
  fallbackPath: string;
  shouldGetAlternatePath?: (pathname: string) => boolean;
  notFoundPath?: string;
}

export const ProtectedRoute = ({
  fallbackPath,
  notFoundPath,
  shouldGetAlternatePath,
  children,
}: PropsWithChildren<ProtectedRouteProps>) => {
  useAuth();
  const { isAuthenticated } = useAuthStore();
  const { pathname } = useLocation();
  const { isRouteAvailable, enableAccessControl, availableRoutes } =
    useRouteAccessContext();
  const authenticatedPathToNavigate = useMemo(() => {
    const defaultPath = notFoundPath || fallbackPath;

    if (shouldGetAlternatePath?.(pathname))
      return availableRoutes?.[0].href || defaultPath;

    return defaultPath;
  }, [
    availableRoutes,
    shouldGetAlternatePath,
    pathname,
    notFoundPath,
    fallbackPath,
  ]);

  const isCurrentRouteAvailable = isRouteAvailable(pathname);

  return isAuthenticated ? (
    !enableAccessControl || isCurrentRouteAvailable ? (
      <>{children}</>
    ) : (
      <Navigate to={authenticatedPathToNavigate} />
    )
  ) : (
    <Navigate to={fallbackPath} />
  );
};
